/* v8 ignore start */
import { StreamMessageTypes, parseStreamMessage, StreamErrorCode as ExistingStreamErrorCode, } from './types';
import { createConnectionError, createError } from '../utils/errorUtils';
import { ERROR_CODES } from '../constants/errorCodes';
import generateUUID from '../utils/generateUUID';
/**
 * Message type constants including new coordination message types
 */
export const StreamMessageType = Object.assign(Object.assign({}, StreamMessageTypes), { 
    // Add session resumption type for backward compatibility
    SESSION_RESUMPTION: 'SESSION_RESUME_RESPONSE' });
/**
 * Error codes for stream errors including coordination error codes
 */
export const StreamErrorCode = Object.assign(Object.assign({}, ExistingStreamErrorCode), { 
    // Connection errors
    CONNECTION_CLOSED: 'connection_closed', 
    // Server errors
    SERVICE_UNAVAILABLE: 'service_unavailable' });
/**
 * Check if the given message is an error message
 *
 * @param {StreamMessage} message - The message to check
 * @returns {message is ErrorMessage} True if the message is an error message
 */
export const isErrorMessage = (message) => {
    return message.type === StreamMessageType.ERROR;
};
/**
 * Check if the given message is a state sync message
 *
 * @param {StreamMessage} message - The message to check
 * @returns {message is StateSyncMessage} True if the message is a state sync message
 */
export const isStateSyncMessage = (message) => {
    return message.type === StreamMessageType.STATE_SYNC;
};
/**
 * Check if the given message is a session resumption message
 *
 * @param {StreamMessage} message - The message to check
 * @returns {message is SessionResumeResponseMessage} True if the message is a session resumption message
 */
export const isSessionResumptionMessage = (message) => {
    return message.type === StreamMessageTypes.SESSION_RESUME ||
        message.type === 'SESSION_RESUME_RESPONSE';
};
/**
 * Create an error message with cross-component tracking
 *
 * @param {StreamErrorCodeType} code - The error code
 * @param {string} message - The error message
 * @param {any} details - Optional error details
 * @param {string} correlationId - Optional correlation ID for cross-component tracking
 * @param {string[]} errorPath - Optional array of components the error has passed through
 * @returns {ErrorMessage} A protocol-compliant error message
 */
export const createStreamError = (code, message, details, correlationId, errorPath = []) => {
    return {
        type: StreamMessageType.ERROR,
        timestamp: Date.now(),
        messageId: `error_${Date.now()}`,
        correlationId: correlationId || generateUUID(),
        data: {
            code: code,
            message,
            details,
            origin: 'client',
            diagnostics: {
                errorPath: ['client', ...errorPath],
                serviceInfo: {
                    clientTimestamp: Date.now(),
                    clientVersion: process.env.NEXT_PUBLIC_VERSION || 'unknown',
                    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
                }
            }
        }
    };
};
/**
 * Convert a stream error to an enhanced error
 *
 * @param {ErrorMessage} errorMessage - The error message to convert
 * @returns {EnhancedError} An enhanced error object with recovery information
 */
export const streamErrorToEnhancedError = (errorMessage) => {
    var _a;
    return createError(errorMessage.data.code, errorMessage.data.message, Object.assign(Object.assign({}, (errorMessage.data.details || {})), { timestamp: errorMessage.timestamp, messageId: errorMessage.messageId, correlationId: errorMessage.correlationId, errorPath: (_a = errorMessage.data.diagnostics) === null || _a === void 0 ? void 0 : _a.errorPath, diagnostics: errorMessage.data.diagnostics }), {
        possibleCauses: [
            'Network connectivity issues',
            'Server error',
            'Rate limiting'
        ],
        suggestedFixes: [
            'Check network connection',
            'Retry the request',
            'Contact support if the issue persists'
        ]
    }, {
        canAutoRecover: false, // No auto recovery
        recoveryStrategy: 'none' // No recovery strategy
    });
};
/**
 * Create a state sync message
 *
 * @param {string} stateId - The state ID
 * @param {string[]} messageIds - Array of message IDs in the state
 * @param {number} stateVersion - The state version number
 * @param {string} stateHash - Optional hash of the state for verification
 * @param {string} correlationId - Optional correlation ID for cross-component tracking
 * @returns {StateSyncMessage} A protocol-compliant state sync message
 */
export const createStateSyncMessage = (stateId, messageIds, stateVersion, stateHash, correlationId) => {
    return {
        type: StreamMessageType.STATE_SYNC,
        timestamp: Date.now(),
        messageId: `sync_${Date.now()}`,
        correlationId: correlationId || generateUUID(),
        data: {
            conversationState: {
                messages: [],
                stateVersion,
                lastUpdated: Date.now(),
                metadata: {
                    stateId,
                    messageIds,
                    stateHash
                }
            },
            source: 'client'
        }
    };
};
/**
 * Create a session resumption message
 *
 * @param {string} sessionId - The session ID to resume
 * @param {string} lastMessageId - The ID of the last message received by the client
 * @param {number} messageCount - The number of messages in the client's conversation history
 * @param {string} lastUserMessage - Optional content of the last user message
 * @param {string} correlationId - Optional correlation ID for cross-component tracking
 * @returns {SessionResumeResponseMessage} A protocol-compliant session resumption message
 */
export const createSessionResumptionMessage = (sessionId, lastMessageId, messageCount, lastUserMessage, correlationId) => {
    return {
        type: StreamMessageType.SESSION_RESUME_RESPONSE,
        timestamp: Date.now(),
        messageId: `resume_${Date.now()}`,
        correlationId: correlationId || generateUUID(),
        data: {
            success: true,
            conversationState: {
                stateVersion: Date.now(),
                lastUpdated: Date.now()
            }
        }
    };
};
/* v8 ignore end */
/**
 * Handles the low-level SSE communication protocol
 */
export class StreamProtocolClient {
    /**
     * Constructs a new StreamProtocolClient with the given configuration
     *
     * @param {StreamProtocolConfig} config - The configuration for the stream protocol client
     */
    constructor(config) {
        this.eventSource = null;
        this.isConnected = false;
        this.lastUrl = '';
        this.isClosed = false;
        this.correlationId = '';
        this.config = config;
        // Ignore autoReconnect setting - always disable automatic reconnection
    }
    /**
     * Establishes a connection to the stream endpoint
     *
     * @param {string} url - The URL of the stream endpoint
     */
    connect(url) {
        // Extract correlation ID from URL if present
        try {
            const urlObj = new URL(url);
            this.correlationId = urlObj.searchParams.get('x-correlation-id') || 'no-correlation-id';
        }
        catch (e) {
            this.correlationId = 'invalid-url';
        }
        console.debug(`[${this.correlationId}] Connecting to stream at ${url}`);
        this.lastUrl = url;
        this.isClosed = false;
        this.disconnect();
        try {
            if (!url) {
                throw new Error('Invalid URL: URL cannot be empty');
            }
            console.debug(`[${this.correlationId}] Creating new EventSource connection`);
            this.eventSource = new EventSource(url);
            this.setupEventHandlers();
            /**
             * Event handler for when the stream connection is opened
             */
            this.eventSource.onopen = () => {
                var _a, _b;
                this.isConnected = true;
                console.debug(`[${this.correlationId}] Stream connection opened`);
                if (((_a = this.eventSource) === null || _a === void 0 ? void 0 : _a.readyState) !== EventSource.OPEN) {
                    const error = createConnectionError('Connection state mismatch', {
                        component: 'StreamProtocolClient',
                        origin: 'client',
                        expectedState: 'OPEN',
                        actualState: this.getReadyStateString((_b = this.eventSource) === null || _b === void 0 ? void 0 : _b.readyState),
                        url: this.lastUrl
                    });
                    console.error(`[${this.correlationId}] Connection state mismatch:`, error);
                    this.handleError(error);
                    return;
                }
            };
            /**
             * Event handler for when an error occurs
             *
             * @param {Event} event - The event that triggered the error
             */
            this.eventSource.onerror = (event) => {
                if (this.isClosed ||
                    !this.eventSource ||
                    this.eventSource.readyState === EventSource.CLOSED) {
                    console.debug(`[${this.correlationId}] Ignoring error event on closed connection`);
                    return;
                }
                const error = this.createErrorFromEvent(event);
                console.error(`[${this.correlationId}] Stream error:`, error);
                this.handleError(error);
                this.disconnect();
            };
        }
        catch (err) {
            console.error(`[${this.correlationId}] Failed to create EventSource:`, err);
            const error = createConnectionError('Failed to create EventSource connection', {
                origin: 'client',
                component: 'StreamProtocolClient',
                url: this.lastUrl,
                originalError: err instanceof Error ? err.message : String(err)
            });
            this.config.onError(error);
        }
    }
    /**
     * Helper to convert readyState to string
     *
     * @param {number} [readyState] - The readyState value to convert
     * @returns {string} The string representation of the readyState
     */
    getReadyStateString(readyState) {
        if (readyState === undefined)
            return 'UNDEFINED';
        switch (readyState) {
            case EventSource.CONNECTING: return 'CONNECTING';
            case EventSource.OPEN: return 'OPEN';
            case EventSource.CLOSED: return 'CLOSED';
            default: return `UNKNOWN(${readyState})`;
        }
    }
    /**
     * Creates an enhanced error from an event
     *
     * @param {Event} event - The event that triggered the error
     * @returns {EnhancedError} The enhanced error object
     */
    createErrorFromEvent(event) {
        // If it's an event with target, try to extract more information
        if (event instanceof Event && event.target instanceof EventSource) {
            const eventSource = event.target;
            const readyState = eventSource.readyState;
            const readyStateString = this.getReadyStateString(readyState);
            // Common details for all error types
            const commonDetails = {
                origin: 'client',
                url: eventSource.url || this.lastUrl,
                state: readyStateString,
                withCredentials: eventSource.withCredentials,
                eventType: event.type,
                timestamp: event.timeStamp
            };
            // Check if it's a connection error
            if (readyState === EventSource.CONNECTING) {
                return createConnectionError('Failed to establish connection to the server', Object.assign(Object.assign({}, commonDetails), { component: 'StreamProtocolClient' }));
            }
            // Check if connection was closed
            if (readyState === EventSource.CLOSED) {
                return createError(ERROR_CODES.CONNECTION_CLOSED, 'Connection was closed', Object.assign(Object.assign({}, commonDetails), { component: 'StreamProtocolClient', wasConnected: this.isConnected }), {
                    possibleCauses: [
                        'Server closed the connection',
                        'Network connectivity issues',
                        'Timeout or server error'
                    ],
                    suggestedFixes: [
                        'Check network connectivity',
                        'Verify server status',
                        'Increase connection timeout'
                    ],
                    documentationLinks: [
                        {
                            title: 'Connection Troubleshooting',
                            url: 'https://mainio.dev/documentation/connection'
                        }
                    ]
                }, {
                    canAutoRecover: false,
                    recoveryStrategy: 'none',
                    recoveryHint: 'Manual reconnection is required to resume the connection.'
                });
            }
            // Handle error in OPEN state
            if (readyState === EventSource.OPEN) {
                return createError(ERROR_CODES.CONNECTION_ERROR, 'Error occurred while connection was open', Object.assign(Object.assign({}, commonDetails), { component: 'StreamProtocolClient' }), {
                    possibleCauses: [
                        'Temporary network disruption',
                        'Server returned an error response',
                        'Connection timeout'
                    ],
                    suggestedFixes: [
                        'Check network connectivity',
                        'Verify request parameters',
                        'Check server logs for errors'
                    ]
                }, {
                    canAutoRecover: false,
                    recoveryStrategy: 'none',
                    recoveryHint: 'Manual reconnection is required to resume the connection.'
                });
            }
        }
        // Default error for unknown scenarios
        return createError(ERROR_CODES.CONNECTION_ERROR, 'Unknown connection error occurred', {
            origin: 'client',
            component: 'StreamProtocolClient',
            url: this.lastUrl,
            timestamp: Date.now(),
            eventType: event instanceof Event ? event.type : 'unknown'
        }, {
            possibleCauses: [
                'Network connectivity issues',
                'Server unavailable',
                'Client-side error'
            ],
            suggestedFixes: [
                'Check network connectivity',
                'Verify server status',
                'Restart the application'
            ]
        }, {
            canAutoRecover: false,
            recoveryStrategy: 'none',
            recoveryHint: 'Manual reconnection is required to resume the connection.'
        });
    }
    /**
     * Disconnects from the stream
     */
    disconnect() {
        if (this.eventSource) {
            console.debug(`[${this.correlationId}] Disconnecting stream`);
            this.isClosed = true;
            this.eventSource.close();
            this.eventSource = null;
        }
        if (this.isConnected) {
            this.isConnected = false;
            if (this.config.onDisconnect) {
                console.debug(`[${this.correlationId}] Calling onDisconnect handler`);
                this.config.onDisconnect();
            }
        }
    }
    /**
     * Sets up event handlers for the EventSource
     */
    setupEventHandlers() {
        if (!this.eventSource)
            return;
        console.debug(`[${this.correlationId}] Setting up event handlers`);
        this.eventSource.addEventListener('message', (event) => {
            // Identify the end of the stream. This is a custom message from the server
            // to indicate that the stream is complete.
            if (event.data === '[DONE]') {
                console.debug(`[${this.correlationId}] Received [DONE] message, closing connection`);
                this.disconnect();
                return;
            }
            try {
                const message = parseStreamMessage(event.data);
                console.debug(`[${this.correlationId}] Received message:`, {
                    type: message.type,
                    messageId: message.messageId,
                    timestamp: message.timestamp
                });
                this.config.onMessage(message);
            }
            catch (err) {
                console.error(`[${this.correlationId}] Failed to parse message:`, err);
                const error = createError(ERROR_CODES.INVALID_RESPONSE, 'Failed to parse stream message', {
                    origin: 'client',
                    component: 'StreamProtocolClient',
                    data: event.data,
                    originalError: err instanceof Error ? err.message : String(err)
                });
                this.handleError(error);
            }
        });
        this.eventSource.addEventListener('error_message', (event) => {
            try {
                console.debug(`[${this.correlationId}] Received error_message event`);
                const errorData = JSON.parse(event.data);
                const enhancedError = {
                    code: errorData.code || ERROR_CODES.SERVER_ERROR,
                    message: errorData.message || 'Server error',
                    details: Object.assign({ origin: 'server', component: 'StreamProtocolClient', timestamp: Date.now() }, (errorData.details || {})),
                    recovery: {
                        canAutoRecover: false,
                        recoveryStrategy: 'none',
                        recoveryHint: 'Manual reconnection is required to resume the connection.'
                    }
                };
                console.error(`[${this.correlationId}] Server error:`, enhancedError);
                this.config.onError(enhancedError);
                this.disconnect();
            }
            catch (err) {
                console.error(`[${this.correlationId}] Failed to parse error_message:`, err);
                this.handleError(err);
            }
        });
    }
    /**
     * Handles protocol-level errors
     */
    handleError(error) {
        // If it's already an EnhancedError, use it directly
        if (typeof error === 'object' &&
            error !== null &&
            'code' in error &&
            'message' in error &&
            'details' in error) {
            this.config.onError(error);
            return;
        }
        // Handle Error objects
        if (error instanceof Error) {
            const enhancedError = createError(ERROR_CODES.UNKNOWN_ERROR, error.message, {
                origin: 'client',
                component: 'StreamProtocolClient',
                stack: error.stack,
                name: error.name
            });
            this.config.onError(enhancedError);
            return;
        }
        // Handle event objects (already handled in createErrorFromEvent)
        if (error instanceof Event) {
            const errorFromEvent = this.createErrorFromEvent(error);
            this.config.onError(errorFromEvent);
            return;
        }
        // Last resort for unknown error types
        const errorDetails = typeof error === 'object' ? error : { message: String(error) };
        const genericError = createError(ERROR_CODES.UNKNOWN_ERROR, 'Unknown error occurred', {
            origin: 'client',
            component: 'StreamProtocolClient',
            details: errorDetails
        });
        this.config.onError(genericError);
    }
    /**
     * Closes the stream properly.
     */
    closeStream() {
        this.isClosed = true;
        this.disconnect();
    }
    /**
     * Checks if the client is currently connected
     *
     * @returns {boolean} True if connected, false otherwise
     */
    isStreamConnected() {
        return this.isConnected && this.eventSource !== null;
    }
    /**
     * Cleans up resources used by the client
     */
    dispose() {
        this.closeStream();
    }
}
//# sourceMappingURL=stream-protocol.js.map